import { Capacitor, registerPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

export interface MuseFlowAudioPluginInterface {
  initialize(): Promise<void>;
  warmUp(): Promise<void>;
  startPlayback(options: {
    bpm: number;
    timeSigNumerator: number;
    timeSigDenominator: number;
    countInMeasures: number;
    startMeasure: number;
    pickupOffsetMeasures?: number;
    tickNoteType: number;
  }): Promise<{ startTime: number }>;
  pausePlayback(): Promise<void>;
  setBpm(options: { bpm: number }): Promise<void>;
  setMetronomeEnabled(options: { enabled: boolean }): Promise<void>;
  setDownbeatsEnabled(options: { enabled: boolean }): Promise<void>;
  setPianoEnabled(options: { enabled: boolean }): Promise<void>;
  playSoundEffect(options: { name: string }): Promise<{ duration: number }>;
  playNarration(options: {
    file: string;
    startTime: number;
    endTime: number;
    rate?: number;
  }): Promise<void>;
  stopNarration(): Promise<void>;
  getCurrentTime(): Promise<{ time: number }>;
  isAvailable(): Promise<{ available: boolean }>;
  addListener(
    eventName: 'tickEvent',
    handler: (data: { ticks: number; currentTime: number }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(eventName: 'interrupted', handler: () => void): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'interruptionEnded',
    handler: (data: { shouldResume: boolean }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'nativeError',
    handler: (data: { message: string; code: string; context?: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const MuseFlowAudio = registerPlugin<MuseFlowAudioPluginInterface>('MuseFlowAudio');

export const isMuseFlowAudioPluginAvailable = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('MuseFlowAudio');
};

const NATIVE_AUDIO_ROUTE_PREFIXES = [
  '/lesson',
  '/tutorial',
  '/repertoire',
  '/repertoire-play',
  '/sound-output',
  '/piano-debug',
  '/exercises/note-length',
];

export const isNativeAudioRoute = (pathname: string): boolean =>
  NATIVE_AUDIO_ROUTE_PREFIXES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

export default MuseFlowAudio;
